import { OrderedColumnsT } from 'business/definitions';
import {
    DEFAULT_MAP_ZOOM,
    ZERO_MARK_FRANCE_LAT,
    ZERO_MARK_FRANCE_LON,
} from 'business/constants';

import { map, reduce } from 'lodash';
import { isPolygon } from 'helpers/formatHelper';

type GeoPoint = {
    lat: number;
    lon: number;
};

export type EChartGmapOptions = {
    point: GeoPoint;
    zoom: number;
};

type Point3D = {
    x: number;
    y: number;
    z: number;
};

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

const pointRad2Deg = (point: GeoPoint): GeoPoint => ({
    lat: point.lat * RAD2DEG,
    lon: point.lon * RAD2DEG,
});

const pointDeg2Rad = (point: GeoPoint): GeoPoint => ({
    lat: point.lat * DEG2RAD,
    lon: point.lon * DEG2RAD,
});

/// Compute the geographic center of multiple points (in longitude, latitude).
/// see: http://www.geomidpoint.com/calculation.html
export const computeGeoMidPoint = (points: GeoPoint[]): GeoPoint => {
    const cartesians: Point3D[] = map(map(points, pointDeg2Rad), (p) => ({
        x: Math.cos(p.lat) * Math.cos(p.lon),
        y: Math.cos(p.lat) * Math.sin(p.lon),
        z: Math.sin(p.lat),
    }));

    const midPointCartesian: Point3D = reduce(cartesians, (acc, val) => ({
        x: acc.x + val.x,
        y: acc.y + val.y,
        z: acc.z + val.z,
    })) || { x: 0, y: 0, z: 0 };

    return pointRad2Deg({
        lon: Math.atan2(midPointCartesian.y, midPointCartesian.x),
        lat: Math.atan2(
            midPointCartesian.z,
            Math.sqrt(midPointCartesian.x ** 2 + midPointCartesian.y ** 2),
        ),
    });
};

/// Compute the distance between two geographic point at the surface of the earth (arc) in km.
/// see: https://www.movable-type.co.uk/scripts/latlong.html
const geoDistance = (p1: GeoPoint, p2: GeoPoint): number => {
    const earthRadius = 6371.0;
    const l1 = p1.lat * DEG2RAD;
    const l2 = p2.lat * DEG2RAD;
    const dLon = (p1.lon - p2.lon) * DEG2RAD;
    const d =
        Math.acos(
            Math.sin(l1) * Math.sin(l2) +
                Math.cos(l1) * Math.cos(l2) * Math.cos(dLon),
        ) * earthRadius;

    return d;
};

/// Find the two most farther point in the list.
const fartherPoints = (points: GeoPoint[]): [GeoPoint, GeoPoint] => {
    if (points.length === 2) {
        return [points[0], points[1]];
    }

    let p1 = points[0];
    let p2 = points[1];
    let maxDist = 0;

    /// Super slow O(N²) but simple impl
    /// Otherwise for a O(N log N) impl: Convex Hull + rotating caliper algo
    /// link: https://www.baeldung.com/cs/most-distant-pair-of-points
    /// Because it's O(N²), we limit the computation to the first 20 points.
    const maxSearch = points.length > 20 ? 20 : points.length;
    for (let i = 0; i < maxSearch - 1; i += 1) {
        for (let n = i + 1; n < maxSearch; n += 1) {
            const a = points[i];
            const b = points[n];
            const distance = geoDistance(a, b);
            if (distance > maxDist) {
                maxDist = distance;
                p1 = a;
                p2 = b;
            }
        }
    }

    return [p1, p2];
};

const remap = (
    value: number,
    low1: number,
    high1: number,
    low2: number,
    high2: number,
) => low2 + ((high2 - low2) * (value - low1)) / (high1 - low1);

/// Given a set of points, return a Google Map's zoom level that should display all of them
/// if the view is aligned at the midpoint.
export const estimateZoomLevel = (points: GeoPoint[]): number => {
    if (points.length < 2) {
        return DEFAULT_MAP_ZOOM;
    }
    const box = fartherPoints(points);
    const maxDist = geoDistance(box[0], box[1]);
    // A rough estimate based on trial and error. Not acurate nor scientific but fast.
    const zoom = Math.floor(
        remap(Math.log2(maxDist / Math.log(2)), -3.0, 10.0, 19, 1),
    );
    return zoom;
};

const DEFAULT_GMAP_OPTIONS = {
    point: { lon: ZERO_MARK_FRANCE_LON, lat: ZERO_MARK_FRANCE_LAT },
    zoom: DEFAULT_MAP_ZOOM,
};

export const getGmapOptionsFromCoordinate = (
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
): EChartGmapOptions => {
    if (!orderedColumns.x) {
        return DEFAULT_GMAP_OPTIONS;
    }
    const xKey = orderedColumns.x.key;
    const points: GeoPoint[] = data
        .map((d) => {
            const coord = (d[xKey] as string).split(' - ');
            const lon = Number(coord[0]);
            const lat = Number(coord[1]);
            if (Number.isNaN(lon) || Number.isNaN(lat)) {
                return undefined;
            }
            return { lon: Number(coord[0]), lat: Number(coord[1]) };
        })
        .filter((p) => p !== undefined) as GeoPoint[];
    const midPoint = computeGeoMidPoint(points);
    const zoomLevel = estimateZoomLevel(points);
    return { point: midPoint, zoom: zoomLevel };
};

export const getGmapOptionsFromPolygon = (
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
): EChartGmapOptions => {
    if (!orderedColumns.x) {
        return DEFAULT_GMAP_OPTIONS;
    }
    const xKey = orderedColumns.x.key;

    const points: GeoPoint[] = data
        .map((d) => {
            const polygons = d[xKey];
            if (!isPolygon(polygons)) {
                return undefined;
            }
            const polygonsPoints = polygons[0][0].map((coord) => ({
                lon: coord[0],
                lat: coord[1],
            }));
            return computeGeoMidPoint(polygonsPoints);
        })
        .filter((p) => p !== undefined) as GeoPoint[];

    if (points.length === 0) {
        return DEFAULT_GMAP_OPTIONS;
    }

    const point = computeGeoMidPoint(points);
    const zoom = estimateZoomLevel(points);
    return { point, zoom };
};
